/**
 * Utility functions for interacting with the Auth0 Management API
 */

/**
 * Updates a user's metadata in Auth0
 * @param {string} userId - The user's Auth0 ID
 * @param {object} userMetadata - The user metadata to update
 * @param {function} getAccessTokenSilently - Function to get an access token
 * @returns {Promise<object>} - The updated user object
 */
export const updateUserMetadata = async (userId, userMetadata, getAccessTokenSilently) => {
  try {
    console.log('Updating user metadata for user:', userId);
    console.log('Auth0 Domain:', process.env.GATSBY_AUTH0_DOMAIN);
    console.log('Auth0 Audience:', process.env.GATSBY_AUTH0_AUDIENCE);
    // Get an access token with the update:current_user_metadata scope
    // Note: This requires proper configuration in Auth0 dashboard
    // 1. Enable the Management API in your Auth0 application
    // 2. Grant the application the required permissions
    // 3. Configure the API to allow these scopes
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.GATSBY_AUTH0_AUDIENCE || `https://${process.env.GATSBY_AUTH0_DOMAIN}/api/v2/`,
        scope: 'update:current_user_metadata read:current_user',
      },
      detailedResponse: true,
    });

    // Extract the actual token from the detailed response
    const token = accessToken.access_token || accessToken;

    // Make a PATCH request to the Auth0 Management API
    const response = await fetch(`https://${process.env.GATSBY_AUTH0_DOMAIN}/api/v2/users/${userId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_metadata: userMetadata }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to update user metadata: ${errorData.message || response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user metadata:', error);
    throw error;
  }
};

/**
 * Gets a user's metadata from Auth0
 * @param {string} userId - The user's Auth0 ID
 * @param {function} getAccessTokenSilently - Function to get an access token
 * @returns {Promise<object>} - The user's metadata
 */
export const getUserMetadata = async (userId, getAccessTokenSilently) => {
  try {
    // Get an access token with the read:current_user scope
    // Note: This requires proper configuration in Auth0 dashboard
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.GATSBY_AUTH0_AUDIENCE || `https://${process.env.GATSBY_AUTH0_DOMAIN}/api/v2/`,
        scope: 'read:current_user',
      },
      detailedResponse: true,
    });

    // Extract the actual token from the detailed response
    const token = accessToken.access_token || accessToken;

    // Make a GET request to the Auth0 Management API
    const response = await fetch(`https://${process.env.GATSBY_AUTH0_DOMAIN}/api/v2/users/${userId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to get user metadata: ${errorData.message || response.statusText}`);
    }

    const userData = await response.json();
    return userData.user_metadata || {};
  } catch (error) {
    console.error('Error getting user metadata:', error);
    throw error;
  }
};
